'use client';

import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, useRouter, useSearchParams } from 'next/navigation';
import Image from 'next/image';
import Link from 'next/link';
import { ArrowLeft, Users, Calendar, Globe, Share2, UserPlus, UserMinus, X, ImagePlus, Trash2, Edit, Heart, Mail } from 'lucide-react';
import { useAuth } from '@/context/AuthContext/AuthContext';
import { useAuthModal } from '@/context/AuthModalContext';
import { useGroupStore } from '@/store/groupStore';
import { Group, GroupRole, getGroupCategories } from '@/types';
import {
  Toast,
  ReportButton,
  Modal,
  BulkEmailModal,
  CrisisModal,
  InappropriateLanguageModal,
  InappropriateLanguageBanner,
  GroupSettingToggle,
  HiddenListGate,
} from '@/components/ui';
import { useBulkEmail } from '@/hooks/useBulkEmail';
import { useContentModeration } from '@/hooks/useContentModeration';
import { EventsTab, JobsTab, ForumTab, GroupPostsTab, VideosTab } from '@/components/group-tabs';
import { isTabType, type TabType } from './tabTypes';
import { ImageCarousel } from '@/components/news';
import { useLanguage } from '@/context/LanguageContext';
import { getGroupCategoryLabelI18n, sortByLabel } from '@/lib/taxonomy-labels';
import { ActivityLogger } from '@/lib/activityLog';
import {
  getImageUploadCopy,
  MAX_MULTI_IMAGE_UPLOAD_COUNT,
  prepareAvatarUpload,
  prepareImageUploads,
} from '@/lib/image-upload';
import { getStrapiImageUrl } from '@/lib/image-utils';
import { fetchStrapiCollection } from '@/lib/strapi';
import { linkifyText } from '@/lib/linkify';
import { getInitials, normalizeExternalUrl } from '@/utils';

const baseUrl = '/api/strapi';

interface GroupMemberDetail {
  id: number;
  documentId: string;
  role: GroupRole;
  approvalStatus: 'pending' | 'approved' | 'canceled';
  createdAt: string;
  user: {
    id: number;
    username: string;
    displayName?: string;
    initials?: string;
    avatarColor?: string;
    avatarUrl?: string;
    ageGroup?: 'age_18_plus' | 'under_18';
  };
}

export default function GroupDetailPage() {
  const { t, language } = useLanguage();
  const params = useParams();
  const router = useRouter();
  const searchParams = useSearchParams();
  const documentId = params.documentId as string;
  const initialTab: TabType = isTabType(searchParams.get('tab')) ? (searchParams.get('tab') as TabType) : 'posts';
  const { currentUser, users, isFavoriteGroup, toggleFavoriteGroup } = useAuth();
  const { openAuthModal } = useAuthModal();
  const {
    fetchGroupMembers,
    fetchMyMemberships,
    updateGroupInStore,
    updateGroup,
    deleteGroup,
    categories: availableCategories,
    fetchCategories: fetchGroupCategories,
  } = useGroupStore();

  // Drive the edit-mode dropdown from the canonical store list (MWBI-798).
  useEffect(() => {
    void fetchGroupCategories();
  }, [fetchGroupCategories]);

  const [group, setGroup] = useState<Group | null>(null);
  const [members, setMembers] = useState<GroupMemberDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);

  const selectTab = useCallback((tab: TabType) => {
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams.toString());
    if (tab === 'posts') {
      next.delete('tab');
    } else {
      next.set('tab', tab);
    }
    const qs = next.toString();
    router.replace(qs ? `?${qs}` : `/groups/${documentId}`, { scroll: false });
  }, [router, searchParams, documentId]);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [joining, setJoining] = useState(false);
  const [showJoinConfirm, setShowJoinConfirm] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    rulesText: '',
    categorySlugs: [] as string[],
    websiteLink: '',
    acceptingMembers: true,
    memberApprovalMode: 'manual' as 'auto' | 'manual',
    // MWBI-890 — the Edit Group form is now the single place these are set.
    // The old per-tab Show/Hide buttons were removed with this change.
    memberListVisible: true,
    eventListVisible: true,
  });
  // Avatar edit state
  const [newAvatar, setNewAvatar] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  // Multi-image edit state
  const MAX_EDIT_IMAGES = MAX_MULTI_IMAGE_UPLOAD_COUNT;
  const [editExistingImages, setEditExistingImages] = useState<{ id: number; url: string }[]>([]);
  const [editNewImages, setEditNewImages] = useState<File[]>([]);
  const [editNewPreviews, setEditNewPreviews] = useState<string[]>([]);
  const [editImagesError, setEditImagesError] = useState<string | null>(null);
  const editImagesInputRef = useRef<HTMLInputElement>(null);

  // Get user role from local members state (not from store which may be stale)
  const userMembership = currentUser ? members.find(m => m.user.id === currentUser.id && m.approvalStatus !== 'canceled') : null;
  const userRole = userMembership?.role ?? null;
  const isMember = userMembership?.approvalStatus === 'approved';
  const isPending = userMembership?.approvalStatus === 'pending';
  const isAdmin = userRole === 'admin';
  const isFavoriteThisGroup = isFavoriteGroup(documentId);

  // Permission check for editing
  const isSiteAdmin = currentUser?.role?.type === 'admin' || currentUser?.role?.type === 'superadmin';
  const canEdit = isAdmin || isSiteAdmin;
  const canSendBulkEmail = isAdmin || isSiteAdmin;

  const MAX_CATEGORIES = 3;
  const uploadCopy = getImageUploadCopy(language, 'multiple');
  const avatarUploadCopy = getImageUploadCopy(language, 'avatar');

  // Content moderation over the edit form's human-text fields (MWBI-868),
  // reusing the shared MWBI-866 hook. Scan name + description + rulesText only
  // (joined with `\n` so the `\b...\b` inappropriate regex and the crisis
  // substring check never match across fields); websiteLink (a URL) is excluded.
  // Unlike CreateNewsModal — which unmounts when closed — this page is
  // persistent, so the scanned text is gated on `isEditing` to keep detection
  // side-effects (auto-opening a modal) from firing outside the edit form.
  const moderationText = isEditing
    ? `${editForm.name}\n${editForm.description}\n${editForm.rulesText}`
    : '';
  const {
    hasInappropriate,
    showCrisisModal,
    showInappropriateModal,
    dismissCrisisModal,
    dismissInappropriateModal,
  } = useContentModeration(moderationText);

  // Close any open moderation modal when leaving edit mode. The page stays
  // mounted across the read/edit toggle and only stops *rendering* the modals
  // (the JSX is gated on `isEditing`); the hook's open-state otherwise persists
  // and would resurface a stale modal over the clean re-seeded form on the next
  // edit session (reachable via keyboard nav). Mirrors MWBI-870 / PR #477.
  useEffect(() => {
    if (!isEditing) {
      dismissCrisisModal();
      dismissInappropriateModal();
    }
  }, [isEditing, dismissCrisisModal, dismissInappropriateModal]);

  const handleStartEdit = () => {
    if (!group) return;
    setEditForm({
      name: group.name,
      description: group.description || '',
      rulesText: group.rulesText || '',
      categorySlugs: getGroupCategories(group).map((c) => c.slug),
      websiteLink: group.websiteLink || '',
      acceptingMembers: group.acceptingMembers ?? true,
      memberApprovalMode: group.memberApprovalMode ?? 'manual',
      // Legacy rows created before these columns existed have no value; a
      // missing flag means visible, matching the server-side filters which
      // treat NULL as visible (cms/server/src/utils/visibility-filters.ts).
      memberListVisible: group.memberListVisible ?? true,
      eventListVisible: group.eventListVisible ?? true,
    });
    // Initialize existing images from the group
    const existingImgs = group.images && group.images.length > 0
      ? group.images.map(img => ({ id: img.id, url: img.url }))
      : group.coverImage?.id && group.coverImage?.url
        ? [{ id: group.coverImage.id, url: group.coverImage.url }]
        : [];
    setEditExistingImages(existingImgs);
    setEditNewImages([]);
    setEditNewPreviews([]);
    setEditImagesError(null);
    setNewAvatar(null);
    setAvatarPreview(null);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    editNewPreviews.forEach(url => URL.revokeObjectURL(url));
    setEditExistingImages([]);
    setEditNewImages([]);
    setEditNewPreviews([]);
    setNewAvatar(null);
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    setAvatarPreview(null);
  };

  const handleSaveEdit = async () => {
    // Defense-in-depth: the Save button is already disabled when the name is
    // empty/whitespace or `hasInappropriate`, but guard here too so neither
    // invariant can be bypassed (e.g. a programmatic click on a disabled button).
    if (!group || saving || !editForm.name.trim() || hasInappropriate) return;
    setSaving(true);
    const result = await updateGroup(documentId, {
      name: editForm.name,
      description: editForm.description,
      rulesText: editForm.rulesText.trim(),
      categorySlugs: editForm.categorySlugs,
      websiteLink: editForm.websiteLink.trim(),
      acceptingMembers: editForm.acceptingMembers,
      memberApprovalMode: editForm.memberApprovalMode,
      memberListVisible: editForm.memberListVisible,
      eventListVisible: editForm.eventListVisible,
      avatar: newAvatar,
      existingImageIds: editExistingImages.map(img => img.id),
      newImages: editNewImages,
    });
    setSaving(false);
    if (result.success) {
      setToast({ message: t.groupDetail.groupUpdated, type: 'success' });
      setIsEditing(false);
      if (currentUser) {
        ActivityLogger.editGroup(currentUser, editForm.name, documentId);
      }
      await refreshGroup();
    } else {
      setToast({ message: result.error || t.groupDetail.failedToUpdate, type: 'error' });
    }
    setTimeout(() => setToast(null), 3000);
  };

  const handleDeleteGroup = async () => {
    if (!group) return;
    const success = await deleteGroup(documentId);
    if (success) {
      setToast({ message: t.groupDetail.groupDeleted, type: 'success' });
      if (currentUser) {
        ActivityLogger.deleteGroup(currentUser, group.name, documentId);
      }
      setTimeout(() => router.push('/groups'), 1500);
    } else {
      setToast({ message: t.groupDetail.failedToDelete, type: 'error' });
      setShowDeleteConfirm(false);
    }
    setTimeout(() => setToast(null), 3000);
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const { file: prepared, compressed, error } = await prepareAvatarUpload(file, language);
    if (error) {
      setToast({ message: error, type: 'error' });
      setTimeout(() => setToast(null), 3000);
      return;
    }
    if (compressed) {
      setToast({ message: avatarUploadCopy.compressedNotice, type: 'success' });
      setTimeout(() => setToast(null), 3000);
    }
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    setNewAvatar(prepared);
    setAvatarPreview(URL.createObjectURL(prepared));
    if (avatarInputRef.current) avatarInputRef.current.value = '';
  };

  const handleEditImagesAdd = async (files: FileList | File[]) => {
    const allFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
    const totalCurrent = editExistingImages.length + editNewImages.length;
    const available = Math.max(0, MAX_EDIT_IMAGES - totalCurrent);
    const toAdd = allFiles.slice(0, available);
    if (toAdd.length === 0) return;
    const { prepared, anyCompressed, error } = await prepareImageUploads(toAdd, language);
    if (error) { setEditImagesError(error); return; }
    setEditImagesError(null);
    if (anyCompressed) {
      setToast({ message: uploadCopy.compressedNotice, type: 'success' });
      setTimeout(() => setToast(null), 3000);
    }
    setEditNewImages(prev => [...prev, ...prepared]);
    setEditNewPreviews(prev => [...prev, ...prepared.map(f => URL.createObjectURL(f))]);
    if (editImagesInputRef.current) editImagesInputRef.current.value = '';
  };

  const removeEditExistingImage = (index: number) => {
    setEditExistingImages(prev => prev.filter((_, i) => i !== index));
  };

  const removeEditNewImage = (index: number) => {
    const url = editNewPreviews[index];
    if (url) URL.revokeObjectURL(url);
    setEditNewImages(prev => prev.filter((_, i) => i !== index));
    setEditNewPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleFavoriteGroup = async () => {
    if (!currentUser) {
      openAuthModal();
      return;
    }
    const result = await toggleFavoriteGroup(documentId);
    if (!result.success) {
      setToast({ message: result.error || 'Failed to save favorite', type: 'error' });
      setTimeout(() => setToast(null), 3000);
    }
  };

  // Function to refresh group data from backend
  const refreshGroup = useCallback(async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const groupRes = await fetch(`${baseUrl}/groups/${documentId}?populate[coverImage]=true&populate[avatar]=true&populate[images]=true&populate[categories][fields][0]=id&populate[categories][fields][1]=documentId&populate[categories][fields][2]=name&populate[categories][fields][3]=slug&populate[group_members][count]=true&populate[group_members][filters][approvalStatus][$eq]=approved&populate[events][count]=true&populate[events][filters][date][$gte]=${today}&populate[events][filters][deletedAt][$null]=true`);
      if (!groupRes.ok) throw new Error('Group not found');
      const groupJson = await groupRes.json();
      const g = groupJson.data;
      setGroup({
        id: g.id,
        documentId: g.documentId,
        name: g.name,
        description: g.description || '',
        rulesText: g.rulesText || '',
        categories: g.categories ?? [],
        ...(g.category !== undefined && g.category !== null ? { category: g.category } : {}),
        coverImage: g.coverImage,
        avatar: g.avatar ?? undefined,
        images: g.images || [],
        acceptingMembers: g.acceptingMembers ?? true,
        memberListVisible: g.memberListVisible ?? true,
        eventListVisible: g.eventListVisible ?? true,
        memberApprovalMode: (g.memberApprovalMode ?? 'manual') as 'auto' | 'manual',
        websiteLink: g.websiteLink,
        tags: g.tags || [],
        videos: g.videos || [],
        memberCount: g.group_members?.count ?? 0,
        eventsCount: g.events?.count ?? 0,
        createdAt: g.createdAt,
        updatedAt: g.updatedAt,
      });
    } catch (error) {
      console.error('Failed to refresh group:', error);
    }
  }, [documentId]);

  useEffect(() => {
    window.scrollTo(0, 0);
    async function fetchGroupData() {
      setLoading(true);
      try {
        // Fetch group details
        await refreshGroup();

        // Fetch every group member (paginated) so pending join requests past
        // the 25-row Strapi cap are never silently dropped (MWBI-725).
        const rawMembers = await fetchStrapiCollection<{
          id: number;
          documentId: string;
          role: GroupRole;
          approvalStatus: 'pending' | 'approved';
          createdAt: string;
          user: GroupMemberDetail['user'];
        }>('group-members', {
          filters: { group: { documentId: { $eq: documentId } } },
          populate: {
            user: { fields: ['id', 'username', 'displayName', 'initials', 'avatarColor', 'avatarUrl', 'ageGroup'] },
          },
        });
        const mappedMembers: GroupMemberDetail[] = rawMembers
          .map((m) => ({
            id: m.id,
            documentId: m.documentId,
            role: m.role,
            approvalStatus: m.approvalStatus || 'approved',
            createdAt: m.createdAt,
            user: m.user,
          }))
          .filter((m) => m.user);
        setMembers(mappedMembers);
      } catch (error) {
        console.error('Error fetching group:', error);
      }
      setLoading(false);
    }
    if (documentId) fetchGroupData();
  }, [documentId, refreshGroup]);

  const handleJoinGroup = () => {
    if (!currentUser) {
      openAuthModal();
      return;
    }
    if (!group || joining || userMembership) return; // Prevent if already has active membership
    setShowJoinConfirm(true);
  };

  const confirmJoinGroup = async () => {
    if (!currentUser || !group || joining || userMembership) return;
    setShowJoinConfirm(false);
    setJoining(true);
    try {
      // Server forces user (from session), role='member', and approvalStatus
      // (pending or approved depending on group.memberApprovalMode). Don't
      // send any of them — AGENTS.md anti-pattern: never send userId in
      // request bodies.
      const res = await fetch(`${baseUrl}/group-members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: { group: { connect: [documentId] } } }),
      });
      if (!res.ok) throw new Error('Failed to join group');
      const json = await res.json();
      const isAutoApproved = json.data?.approvalStatus === 'approved';
      const newMember: GroupMemberDetail = {
        id: json.data.id,
        documentId: json.data.documentId,
        role: 'member',
        approvalStatus: isAutoApproved ? 'approved' : 'pending',
        createdAt: new Date().toISOString(),
        user: { id: currentUser.id, username: currentUser.username, displayName: currentUser.displayName, initials: currentUser.initials, avatarColor: currentUser.avatarColor, avatarUrl: currentUser.avatarUrl },
      };
      setMembers(prev => [...prev, newMember]);
      fetchGroupMembers(true);
      fetchMyMemberships(currentUser.id, true);
      ActivityLogger.joinGroup(currentUser, group.name, group.documentId);
      setToast({ message: isAutoApproved ? t.groupDetail.joinedGroup : t.groupDetail.joinRequestSent, type: 'success' });
    } catch (error) {
      console.error('src/app/groups/[documentId]/page.tsx', error);
      setToast({ message: t.groupDetail.failedToJoin, type: 'error' });
    }
    setJoining(false);
    setTimeout(() => setToast(null), 4000);
  };

  const handleLeaveGroup = async () => {
    if (!currentUser || !group || !userMembership || joining) return;

    if (userMembership.role === 'admin') {
      const approvedAdmins = members.filter(
        (m) => m.approvalStatus === 'approved' && m.role === 'admin'
      );
      if (approvedAdmins.length <= 1) {
        setToast({
          message: 'You must assign at least 1 other member as admin before leaving.',
          type: 'error',
        });
        setTimeout(() => setToast(null), 4000);
        return;
      }
    }

    setShowLeaveConfirm(true);
  };

  const confirmLeaveGroup = async () => {
    if (!currentUser || !group || !userMembership || joining) return;

    setJoining(true);
    try {
      const res = await fetch(`${baseUrl}/group-members/${userMembership.documentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: { approvalStatus: 'canceled' } }),
      });
      if (!res.ok) throw new Error('Failed to leave group');

      setMembers(prev => prev.map(m => m.id === userMembership.id ? { ...m, approvalStatus: 'canceled' } : m));
      await fetchGroupMembers(true);
      if (currentUser) await fetchMyMemberships(currentUser.id, true);
      setToast({ message: 'You left the group.', type: 'success' });
    } catch (error) {
      console.error('src/app/groups/[documentId]/page.tsx', error);
      setToast({ message: 'Failed to leave group', type: 'error' });
    }
    setShowLeaveConfirm(false);
    setJoining(false);
    setTimeout(() => setToast(null), 4000);
  };

  if (loading) return <div className="p-3 md:p-6 text-center text-foreground-muted">{t.groupDetail.loadingGroup}</div>;
  if (!group) return <div className="p-3 md:p-6 text-center text-foreground-muted">{t.groupDetail.groupNotFound}</div>;

  const coverUrl = getStrapiImageUrl(group.coverImage?.url || group.images?.[0]?.url);
  const groupImages = group.images && group.images.length > 0
    ? group.images
    : group.coverImage?.url
      ? [{ id: group.coverImage.id ?? group.id, url: group.coverImage.url, alternativeText: group.coverImage.alternativeText }]
      : [];
  const tags = Array.isArray(group.tags) ? group.tags : [];
  const groupCategories = getGroupCategories(group);
  const foundedDate = new Date(group.createdAt).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', { month: 'long', year: 'numeric' });

  const TABS: { id: TabType; label: string }[] = [
    { id: 'posts', label: t.groupTabs.posts },
    { id: 'forum', label: t.groupTabs.discussions },
    { id: 'events', label: t.nav.events },
    { id: 'videos', label: t.groupTabs.videos },
    { id: 'members', label: t.groupTabs.members },
  ];

  return (
    <div className="max-w-6xl mx-auto px-3 md:px-6 py-3 md:py-6 space-y-6 overflow-hidden">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Top Bar with Actions */}
      <div className="flex items-center justify-between gap-3">
        <Link href="/groups" data-testid="group-detail-back-link" className="inline-flex items-center gap-2 text-sm text-foreground-muted hover:text-foreground transition">
          <ArrowLeft size={16} /> {t.groupDetail.backToGroups}
        </Link>
        <div className="flex items-center gap-2">
          {!isEditing && (
            <button
              onClick={handleFavoriteGroup}
              data-testid="group-detail-favorite-btn"
              className={`inline-flex items-center justify-center w-9 h-9 rounded-md border text-sm transition ${
                isFavoriteThisGroup
                  ? 'bg-red-50 border-red-200 text-red-600'
                  : 'bg-background border-border text-foreground-muted hover:text-foreground'
              }`}
              aria-label={isFavoriteThisGroup ? t.groups.removeFromFavorites : t.groups.addToFavorites}
            >
              <Heart size={16} className={isFavoriteThisGroup ? 'fill-current' : ''} />
            </button>
          )}
          {!isEditing && group && (
            <span data-testid="group-detail-report-btn">
              <ReportButton
                contentType="group"
                contentId={group.documentId}
                contentTitle={group.name}
                className="inline-flex items-center justify-center w-9 h-9 rounded-md border bg-background border-border text-foreground-muted hover:bg-sidebar-hover transition"
              />
            </span>
          )}
          {canEdit && !isEditing && (
            <button
              onClick={handleStartEdit}
              data-testid="group-detail-edit-group-btn"
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:opacity-90 transition"
            >
              <Edit size={16} /> {t.groupDetail.editGroup}
            </button>
          )}
          {isEditing && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowDeleteConfirm(true)}
              data-testid="group-detail-delete-group-btn"
              className="flex items-center gap-2 px-4 py-2 text-red-500 border border-red-300 rounded-md text-sm font-medium hover:bg-red-50 transition"
            >
              <Trash2 size={16} /> {t.groupDetail.deleteGroup}
            </button>
            <button
              onClick={handleCancelEdit}
              data-testid="group-detail-cancel-edit-btn"
              className="px-4 py-2 text-foreground-muted hover:bg-sidebar-hover rounded-md text-sm font-medium transition"
            >
              {t.groupDetail.cancelEdit}
            </button>
            <button
              onClick={handleSaveEdit}
              disabled={saving || !editForm.name.trim() || hasInappropriate}
              data-testid="group-detail-save-changes-btn"
              className="px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:opacity-90 transition disabled:opacity-50"
            >
              {saving ? '...' : t.groupDetail.saveChanges}
            </button>
          </div>
          )}
        </div>
      </div>

      {/* Content moderation (MWBI-868) — edit form only. Gated on `isEditing`
          (which implies `canEdit`, since edit mode is only reachable via the
          canEdit-gated Edit button) so the banner/modals stay visible whenever
          Save can be blocked — matching the Save button, which is gated on
          `isEditing` alone. Crisis is informational and never blocks;
          inappropriate language shows an ack modal + banner and disables Save
          (see the Save button + handleSaveEdit). */}
      {isEditing && (
        <>
          <CrisisModal
            open={showCrisisModal}
            onClose={dismissCrisisModal}
            testIdPrefix="group-detail-crisis"
          />
          <InappropriateLanguageModal
            open={showInappropriateModal}
            onClose={dismissInappropriateModal}
            testIdPrefix="group-detail-inappropriate"
          />
          {hasInappropriate && (
            <InappropriateLanguageBanner testIdPrefix="group-detail-inappropriate" />
          )}
        </>
      )}

      {/* Header */}
      <div
        data-testid="group-detail-hero"
        className="flex flex-col xl:flex-row gap-8 bg-white border border-border rounded-xl p-6 max-xl:overflow-hidden"
      >
        {/* Cover Image — stacks above the info panel below xl (1280px); sits side-by-side only at xl+ (MWBI-851) */}
        <div data-testid="group-detail-cover" className="xl:w-1/2 flex-shrink-0 max-xl:-mx-6 max-xl:w-[calc(100%+3rem)]">
          {isEditing ? (
            <div className="space-y-3">
              {/* Multi-image management */}
              <div>
                <p className="text-sm font-medium text-foreground mb-2">
                  {t.modals.group.groupPhotos} ({editExistingImages.length + editNewImages.length}/{MAX_EDIT_IMAGES})
                </p>
                {(editExistingImages.length > 0 || editNewImages.length > 0) && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
                    {editExistingImages.map((img, index) => (
                      <div key={img.id} className="relative aspect-square rounded-lg overflow-hidden border border-border bg-background-secondary">
                        <Image src={getStrapiImageUrl(img.url) ?? ''} alt={`Photo ${index + 1}`} fill className="object-contain" unoptimized />
                        <button
                          type="button"
                          onClick={() => removeEditExistingImage(index)}
                          data-testid={`group-detail-remove-existing-image-btn-${index}`}
                          className="absolute top-1 right-1 bg-error text-white rounded-full p-0.5"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                    {editNewPreviews.map((preview, index) => (
                      <div key={preview} className="relative aspect-square rounded-lg overflow-hidden border border-primary bg-background-secondary">
                        <Image src={preview} alt={`New photo ${index + 1}`} fill className="object-contain" unoptimized />
                        <button
                          type="button"
                          onClick={() => removeEditNewImage(index)}
                          className="absolute top-1 right-1 bg-error text-white rounded-full p-0.5"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => {
                    if (editExistingImages.length + editNewImages.length < MAX_EDIT_IMAGES) {
                      editImagesInputRef.current?.click();
                    }
                  }}
                  disabled={editExistingImages.length + editNewImages.length >= MAX_EDIT_IMAGES}
                  className="flex items-center gap-2 text-sm font-medium text-foreground border border-border px-3 py-1.5 rounded-md hover:bg-sidebar-hover transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ImagePlus size={16} /> {uploadCopy.uploadPrompt}
                </button>
                <p className="text-xs text-caption mt-1">{uploadCopy.fileTypes}</p>
                {editImagesError && <p className="text-xs text-error mt-1">{editImagesError}</p>}
                <input
                  ref={editImagesInputRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  multiple
                  onChange={e => { if (e.target.files) void handleEditImagesAdd(e.target.files); }}
                  data-testid="group-detail-edit-images-input"
                  className="hidden"
                />
              </div>

              {/* Avatar management */}
              <div>
                <p className="text-sm font-medium text-foreground mb-0.5">{t.modals.group.groupAvatar}</p>
                <p className="text-xs text-caption mb-2">{t.modals.group.groupAvatarHint}</p>
                <div className="flex items-center gap-3">
                  <div
                    className="relative w-16 h-16 rounded-full overflow-hidden border border-border flex-shrink-0 flex items-center justify-center text-white text-base font-bold"
                    style={{ backgroundColor: '#00423E' }}
                  >
                    {avatarPreview ? (
                      <Image src={avatarPreview} alt="Avatar" fill className="object-cover" unoptimized />
                    ) : group.avatar?.url ? (
                      <Image src={getStrapiImageUrl(group.avatar.url) ?? ''} alt="Avatar" fill className="object-cover" unoptimized />
                    ) : (
                      getInitials(group.name)
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <button
                      type="button"
                      onClick={() => avatarInputRef.current?.click()}
                      className="text-sm font-medium text-foreground border border-border px-3 py-1.5 rounded-md hover:bg-sidebar-hover transition"
                    >
                      {avatarUploadCopy.uploadPrompt}
                    </button>
                    <p className="text-xs text-caption">{avatarUploadCopy.fileTypes}</p>
                  </div>
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/png,image/jpeg"
                    onChange={handleAvatarChange}
                    data-testid="group-detail-edit-avatar-input"
                    className="hidden"
                  />
                </div>
              </div>
            </div>
          ) : groupImages.length > 0 ? (
            <ImageCarousel images={groupImages} containerClassName="max-xl:rounded-none" />
          ) : (
            <div className="relative h-48 xl:h-64 rounded-lg max-xl:rounded-none overflow-hidden bg-background-secondary">
              <Image src="/NoImage.jpg" alt={group.name} fill className="object-cover" unoptimized />
            </div>
          )}
        </div>

        {/* Group Info — min-w-0 lets this flex child shrink so its content wraps instead of being clipped (MWBI-851) */}
        <div data-testid="group-detail-info" className="flex-1 min-w-0 space-y-3">
          {isEditing ? (
            <input
              type="text"
              value={editForm.name}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              data-testid="group-detail-edit-name-input"
              className="text-3xl font-bold text-foreground bg-transparent border-b-2 border-primary focus:outline-none w-full"
              placeholder={t.modals.group.enterGroupName}
            />
          ) : (
            <div className="flex items-center gap-3">
              <div
                className="relative w-12 h-12 rounded-full overflow-hidden border border-border flex-shrink-0 flex items-center justify-center text-white text-sm font-bold"
                style={{ backgroundColor: '#00423E' }}
              >
                {group.avatar?.url ? (
                  <Image src={getStrapiImageUrl(group.avatar.url) ?? ''} alt={group.name} fill className="object-cover" unoptimized />
                ) : (
                  getInitials(group.name)
                )}
              </div>
              <h1 className="text-3xl font-bold text-foreground min-w-0">{group.name}</h1>
            </div>
          )}

          <div className="space-y-1 text-sm text-foreground-muted">
            <div className="flex items-center gap-2"><Users size={14} /><span>{group.memberCount || members.length} {t.groupTabs.members.toLowerCase()}</span></div>
            <div className="flex items-center gap-2"><Calendar size={14} /><span>{t.groupDetail.founded} {foundedDate}</span></div>
            <div className="flex items-center gap-2" data-testid="group-detail-upcoming-events">
              <Calendar size={14} />
              <span>{t.groupDetail.upcomingEvents} {group.eventsCount ?? 0}</span>
            </div>
            {isEditing ? (
              <div className="flex items-center gap-2">
                <Globe size={14} />
                <input
                  type="url"
                  value={editForm.websiteLink}
                  onChange={(e) => setEditForm({ ...editForm, websiteLink: e.target.value })}
                  data-testid="group-detail-edit-website-input"
                  aria-label={t.groupDetail.websiteLink}
                  className="bg-transparent border-b border-border focus:border-primary focus:outline-none flex-1"
                  placeholder="https://..."
                />
              </div>
            ) : (() => {
              // Allowlist http/https only — an admin could save javascript:
              // or other URI schemes that would execute as XSS on click.
              const safeWebsite = normalizeExternalUrl(group.websiteLink);
              return safeWebsite ? (
                <div className="flex items-center gap-2">
                  <Globe size={14} />
                  <a
                    href={safeWebsite}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="group-detail-website-link"
                    className="text-link-blue hover:underline"
                  >
                    {t.groupDetail.visitWebsite}
                  </a>
                </div>
              ) : null;
            })()}
            <div
              className={`text-sm font-medium ${group.acceptingMembers ? 'text-primary' : 'text-foreground'}`}
              data-testid="group-detail-accepting-status"
            >
              {group.acceptingMembers ? t.groups.acceptingNewMembers : t.groups.groupIsFull}
            </div>
          </div>

          {/* Category */}
          {isEditing ? (
            <div>
              <div className="flex flex-wrap gap-2 mb-2">
                {editForm.categorySlugs.map((slug) => {
                  const cat = availableCategories.find((c) => c.slug === slug);
                  return (
                    <span
                      key={slug}
                      className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium bg-primary/10 text-primary border border-primary/20 rounded-full"
                    >
                      {cat ? getGroupCategoryLabelI18n(t, cat.slug, cat.name) : slug}
                      <X
                        size={14}
                        className="cursor-pointer hover:text-primary-dark"
                        onClick={() => setEditForm({ ...editForm, categorySlugs: editForm.categorySlugs.filter((s) => s !== slug) })}
                      />
                    </span>
                  );
                })}
              </div>
              <select
                value=""
                onChange={(e) => {
                  const value = e.target.value;
                  if (value && !editForm.categorySlugs.includes(value) && editForm.categorySlugs.length < MAX_CATEGORIES) {
                    setEditForm({ ...editForm, categorySlugs: [...editForm.categorySlugs, value] });
                  }
                }}
                disabled={editForm.categorySlugs.length >= MAX_CATEGORIES}
                data-testid="group-detail-edit-category-select"
                className="px-3 py-1.5 border border-border rounded-md text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">
                  {editForm.categorySlugs.length >= MAX_CATEGORIES ? 'Maximum categories selected' : 'Select categories...'}
                </option>
                {sortByLabel(
                  availableCategories.filter((cat) => !cat.archived && !editForm.categorySlugs.includes(cat.slug)),
                  (cat) => getGroupCategoryLabelI18n(t, cat.slug, cat.name),
                  language,
                ).map((cat) => (
                  <option key={cat.slug} value={cat.slug}>
                    {getGroupCategoryLabelI18n(t, cat.slug, cat.name)}
                  </option>
                ))}
              </select>
            </div>
          ) : groupCategories.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {sortByLabel(groupCategories, (cat) => getGroupCategoryLabelI18n(t, cat.slug, cat.name), language).map((cat) => (
                <span key={cat.documentId} className="px-2 py-0.5 text-xs font-medium bg-background-secondary border border-border rounded-full text-foreground-muted">
                  {getGroupCategoryLabelI18n(t, cat.slug, cat.name)}
                </span>
              ))}
            </div>
          ) : null}

          {!isEditing && tags.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {tags.map((tag, i) => (
                <span key={i} className="px-2 py-0.5 text-xs font-medium bg-background-secondary border border-border rounded-full text-foreground-muted">{tag}</span>
              ))}
            </div>
          ) : null}

          {/* MWBI-890 — all four group settings, one shared green switch.
              This form is the single place member-list and event visibility
              can be changed; the old per-tab Show/Hide buttons were removed.
              Previously these two used bespoke bg-primary/bg-border switches
              with hard-coded English "Yes"/"No" labels. */}
          {isEditing && (
            <div className="space-y-4">
              <GroupSettingToggle
                label={t.modals.group.toggleAcceptingMembersAria}
                checked={!editForm.acceptingMembers}
                onChange={(isFull) => setEditForm({ ...editForm, acceptingMembers: !isFull })}
                offLabel={t.modals.group.acceptingMembers}
                onLabel={t.modals.group.groupIsFull}
                testIdPrefix="group-detail-edit-accepting-members"
                disabled={saving}
                disabledReasonId="group-detail-edit-saving-status"
              />
              <GroupSettingToggle
                label={t.modals.group.toggleApprovalModeAria}
                checked={editForm.memberApprovalMode === 'auto'}
                onChange={(isAuto) => setEditForm({ ...editForm, memberApprovalMode: isAuto ? 'auto' : 'manual' })}
                offLabel={t.modals.group.manualReview}
                onLabel={t.modals.group.autoAccept}
                testIdPrefix="group-detail-edit-member-approval"
                disabled={saving}
                disabledReasonId="group-detail-edit-saving-status"
              />
              <GroupSettingToggle
                label={t.modals.group.toggleMemberListAria}
                checked={!editForm.memberListVisible}
                onChange={(hidden) => setEditForm({ ...editForm, memberListVisible: !hidden })}
                offLabel={t.modals.group.showMemberList}
                onLabel={t.modals.group.hideMemberList}
                testIdPrefix="group-detail-edit-member-list"
                disabled={saving}
                disabledReasonId="group-detail-edit-saving-status"
              />
              <GroupSettingToggle
                label={t.modals.group.toggleGroupEventsAria}
                checked={!editForm.eventListVisible}
                onChange={(hidden) => setEditForm({ ...editForm, eventListVisible: !hidden })}
                offLabel={t.modals.group.showGroupEvents}
                onLabel={t.modals.group.hideGroupEvents}
                testIdPrefix="group-detail-edit-group-events"
                disabled={saving}
                disabledReasonId="group-detail-edit-saving-status"
              />
              <p className="text-xs text-foreground-muted leading-relaxed">
                {t.modals.group.groupVisibilityNote}
              </p>
              {/* Announced when the switches go disabled mid-save, and the
                  target of their aria-describedby, so non-visual users get the
                  reason rather than four silently dimmed controls. */}
              {saving && (
                <p
                  id="group-detail-edit-saving-status"
                  role="status"
                  className="text-xs text-foreground-muted"
                  data-testid="group-detail-edit-saving-status"
                >
                  {t.groupDetail.savingSettings}
                </p>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 pt-2">
            {!isMember && !isPending && (
              <button
                onClick={handleJoinGroup}
                disabled={!group.acceptingMembers || joining}
                data-testid="group-detail-join-btn"
                className="flex items-center gap-2 px-4 py-2 bg-action-green text-black rounded-md text-sm font-medium hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <UserPlus size={16} />
                {!group.acceptingMembers ? t.groups.groupIsFull : joining ? t.groupDetail.requesting : t.groupDetail.joinGroup}
              </button>
            )}
            {isPending && userMembership && (
              <button
                onClick={async () => {
                  try {
                    const res = await fetch(`${baseUrl}/group-members/${userMembership.documentId}`, {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ data: { approvalStatus: 'canceled' } }),
                    });
                    if (!res.ok) throw new Error('Failed to cancel request');
                    setMembers(prev => prev.map(m => m.id === userMembership.id ? { ...m, approvalStatus: 'canceled' } : m));
                    fetchGroupMembers(true);
                    if (currentUser) fetchMyMemberships(currentUser.id, true);
                    setToast({ message: t.groupDetail.joinRequestCanceled, type: 'success' });
                  } catch (error) {
                    console.error('src/app/groups/[documentId]/page.tsx', error);
                    setToast({ message: t.groupDetail.failedToCancelRequest, type: 'error' });
                  }
                  setTimeout(() => setToast(null), 4000);
                }}
                data-testid="group-detail-cancel-join-request-btn"
                className="px-4 py-2 bg-action-green text-black rounded-md text-sm font-medium hover:opacity-90 transition cursor-pointer"
              >
                {t.groupDetail.requestPending} ✕
              </button>
            )}
            {isMember && (
              <button
                onClick={handleLeaveGroup}
                disabled={joining}
                data-testid="group-detail-leave-btn"
                className="flex items-center gap-2 px-4 py-2 bg-action-green text-black rounded-md text-sm font-medium hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <UserMinus size={16} />
                {joining ? 'Leaving...' : 'Leave Group'}
              </button>
            )}
            <button
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                setToast({ message: t.groupDetail.linkCopied, type: 'success' });
                setTimeout(() => setToast(null), 3000);
              }}
              data-testid="group-detail-share-btn"
              className="flex items-center gap-2 px-4 py-2 border border-border rounded-md text-sm text-foreground hover:bg-sidebar-hover transition"
            >
              <Share2 size={16} /> {t.common.share}
            </button>
          </div>
        </div>
      </div>
      {/* Rules — only shown in edit mode; hidden in read-only view per product decision */}
      {canEdit && isEditing && (
        <section className="w-full bg-white border border-border rounded-xl p-6 space-y-4">
          <h2 className="text-xl sm:text-2xl font-semibold text-foreground">{t.groupDetail.groupRules}</h2>
          <textarea
            value={editForm.rulesText}
            onChange={(e) => setEditForm({ ...editForm, rulesText: e.target.value })}
            rows={7}
            maxLength={4000}
            data-testid="group-detail-edit-rules-textarea"
            className="w-full text-foreground-muted text-sm bg-transparent border border-border rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-primary resize-y"
          />
          <p className="text-xs text-foreground-muted text-right">{editForm.rulesText.length}/4000</p>
        </section>
      )}

      {/* Description Section */}
      {(isEditing || group.description) && (
        <section className="w-full bg-white border border-border rounded-xl p-6 space-y-4">
          <h2 className="text-xl sm:text-2xl font-semibold text-foreground">{t.groupDetail.aboutUs}</h2>
          {isEditing ? (
            <textarea
              value={editForm.description}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              rows={6}
              data-testid="group-detail-edit-description-textarea"
              className="w-full text-foreground-muted text-sm bg-transparent border border-border rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-primary resize-y"
              placeholder={t.modals.group.describeGroup}
            />
          ) : (
            <div data-testid="group-detail-about-text" className="text-foreground text-base leading-7 whitespace-pre-line">
              {linkifyText(group.description)}
            </div>
          )}
        </section>
      )}

      {/* Tabs */}
      <div className="rounded-xl overflow-hidden">
      <div className="flex gap-2 px-4 md:px-6 py-3 border-b border-border overflow-x-auto">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => selectTab(tab.id)}
            data-testid={`group-detail-${tab.id}-tab`}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition whitespace-nowrap border ${activeTab === tab.id ? 'bg-tab-active-bg border-filter-bg text-black' : 'bg-white border-border text-foreground-muted hover:border-primary hover:text-foreground'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'posts' && (
        <GroupPostsTab
          groupId={group.id}
          groupDocumentId={group.documentId}
          groupName={group.name}
          canCreate={isAdmin || userRole === 'editor'}
        />
      )}
      {activeTab === 'forum' && <ForumTab groupId={group.id} groupDocumentId={group.documentId} groupName={group.name} isMember={isMember} isGroupAdmin={isAdmin || isSiteAdmin} />}
      {activeTab === 'events' && (
        <EventsTab
          groupId={group.id}
          isMember={isMember}
          isSiteAdmin={isSiteAdmin}
          canCreate={isAdmin || userRole === 'editor'}
          eventListVisible={group.eventListVisible}
        />
      )}
      {activeTab === 'videos' && (
        <VideosTab
          groupDocumentId={group.documentId}
          videos={group.videos || []}
          canEdit={isAdmin || userRole === 'editor'}
          onUpdate={async (videos) => {
            // Update local state immediately for instant UI update
            setGroup(prev => prev ? { ...prev, videos } : prev);
            // Refresh the full group data from backend to get the updated id and all relations
            await refreshGroup();
            // Update the group in the store
            updateGroupInStore(group.id, { videos });
          }}
        />
      )}
      {activeTab === 'jobs' && <JobsTab groupId={group.id} groupName={group.name} canPost={isAdmin || userRole === 'editor'} />}
      {activeTab === 'members' && (
        <MembersTab
          members={members}
          isMember={isMember}
          isAdmin={isAdmin}
          canSendBulkEmail={canSendBulkEmail}
          users={users}
          setMembers={setMembers}
          setToast={setToast}
          fetchGroupMembers={fetchGroupMembers}
          group={group}
        />
      )}
      </div>

      {/* Join Confirmation Modal */}
      {showJoinConfirm && group && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowJoinConfirm(false)} />
          <div
            data-testid="group-detail-join-modal"
            className="relative bg-background rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] flex flex-col space-y-4"
          >
            <button
              onClick={() => setShowJoinConfirm(false)}
              className="absolute top-3 right-3 text-foreground-muted hover:text-foreground transition"
              aria-label="Close"
            >
              <X size={18} />
            </button>
            <h3 className="text-lg font-semibold text-foreground">{group.name}</h3>
            <p data-testid="group-detail-join-message" className="text-foreground-muted text-sm">
              {t.groupDetail.joinConfirmMessage}
            </p>
            {group.rulesText && (
              <div
                data-testid="group-detail-join-rules"
                className="mt-2 space-y-1 flex-1 min-h-0 overflow-y-auto"
              >
                <p className="text-sm font-medium text-foreground">{t.groupDetail.groupRules}</p>
                <p className="text-sm text-foreground-muted whitespace-pre-line">{group.rulesText}</p>
              </div>
            )}
            <div className="flex gap-2 justify-end shrink-0">
              <button
                onClick={() => setShowJoinConfirm(false)}
                className="px-4 py-2 text-foreground-muted hover:bg-background-secondary rounded-lg transition"
              >
                {t.common.cancel}
              </button>
              <button
                onClick={confirmJoinGroup}
                data-testid="group-detail-confirm-join-btn"
                className="px-4 py-2 bg-primary text-white rounded-lg hover:opacity-90 transition"
              >
                {t.groupDetail.joinConfirmAgree}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowDeleteConfirm(false)} />
          <div className="relative bg-background rounded-lg p-6 max-w-sm mx-4 space-y-4">
            <h3 className="text-lg font-semibold text-foreground">{t.groupDetail.deleteConfirmTitle}</h3>
            <p className="text-foreground-muted">{t.groupDetail.deleteConfirmMessage}</p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-foreground-muted hover:bg-background-secondary rounded-lg transition"
              >
                {t.common.cancel}
              </button>
              <button
                onClick={handleDeleteGroup}
                data-testid="group-detail-confirm-delete-btn"
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
              >
                {t.groupDetail.confirmDelete}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Leave Group Confirmation Modal */}
      {showLeaveConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowLeaveConfirm(false)} />
          <div className="relative bg-background rounded-lg p-6 max-w-sm mx-4 space-y-4">
            <h3 className="text-lg font-semibold text-foreground">Leave Group</h3>
            <p className="text-foreground-muted">
              Are you sure you want to leave this group?
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowLeaveConfirm(false)}
                className="px-4 py-2 text-foreground-muted hover:bg-background-secondary rounded-lg transition"
              >
                {t.common.cancel}
              </button>
              <button
                onClick={confirmLeaveGroup}
                data-testid="group-detail-confirm-leave-btn"
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
              >
                Leave
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface MembersTabProps {
  members: GroupMemberDetail[];
  isMember: boolean;
  isAdmin: boolean;
  canSendBulkEmail: boolean;
  users: { id: number; avatarUrl?: string; displayName: string; initials: string; avatarColor: string }[];
  setMembers: React.Dispatch<React.SetStateAction<GroupMemberDetail[]>>;
  setToast: React.Dispatch<React.SetStateAction<{ message: string; type: 'success' | 'error' } | null>>;
  fetchGroupMembers: (force?: boolean) => Promise<void>;
  group: Group;
}

function MembersTab({ members, isMember, isAdmin, canSendBulkEmail, users, setMembers, setToast, fetchGroupMembers, group }: MembersTabProps) {
  const { language, t } = useLanguage();
  const baseUrl = '/api/strapi';
  const getUserData = (userId: number) => users.find(u => u.id === userId);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [memberToReject, setMemberToReject] = useState<GroupMemberDetail | null>(null);
  const [rejectionNote, setRejectionNote] = useState('');
  const [sendingRejection, setSendingRejection] = useState(false);
  const [pendingMemberActionValues, setPendingMemberActionValues] = useState<Record<number, string>>({});
  const pendingMembers = useMemo(
    () => members.filter((m) => m.approvalStatus === 'pending'),
    [members]
  );
  const approvedMembers = useMemo(
    () => members.filter((m) => m.approvalStatus === 'approved'),
    [members]
  );
  const approvedAdminCount = useMemo(
    () => approvedMembers.filter((m) => m.role === 'admin').length,
    [approvedMembers]
  );

  const memberListVisible = group.memberListVisible !== false; // Default to true if undefined
  const canViewMembers = isMember || canSendBulkEmail || memberListVisible;

  const showTimedToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const bulkEmail = useBulkEmail({
    groupId: group.id,
    approvedMembers,
    messages: {
      validationError: t.groupTabs.bulkEmailValidationError,
      sent: t.groupTabs.bulkEmailSent,
      sendFailed: t.groupTabs.bulkEmailSendFailed,
      forbidden: t.groupTabs.bulkEmailForbidden,
    },
    onToast: showTimedToast,
  });

  const closeRejectModal = () => {
    if (sendingRejection) return;

    setShowRejectModal(false);
    setMemberToReject(null);
    setRejectionNote('');
  };

  const resetPendingMemberAction = (memberId: number) => {
    setPendingMemberActionValues((prev) => ({ ...prev, [memberId]: '' }));
  };

  const handleAccept = async (member: GroupMemberDetail) => {
    try {
      const res = await fetch(`${baseUrl}/group-members/${member.documentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: { approvalStatus: 'approved' } }),
      });
      if (!res.ok) throw new Error('Failed to accept member');
      setMembers(prev => prev.map(m => m.id === member.id ? { ...m, approvalStatus: 'approved' } : m));
      // Refresh the group store so the new member sees the group in their list
      await fetchGroupMembers(true);
      setToast({ message: `${member.user.displayName || member.user.username} has been accepted!`, type: 'success' });
    } catch (error) {
      console.error('src/app/groups/[documentId]/page.tsx', error);
      setToast({ message: 'Failed to accept member', type: 'error' });
    }
    setTimeout(() => setToast(null), 4000);
  };

  const handleReject = (member: GroupMemberDetail) => {
    resetPendingMemberAction(member.id);
    setMemberToReject(member);
    setRejectionNote('');
    setShowRejectModal(true);
  };

  const handleConfirmReject = async () => {
    if (!memberToReject) return;

    const note = rejectionNote.trim();

    if (!note) {
      setToast({ message: t.groupTabs.rejectionReasonRequired, type: 'error' });
      setTimeout(() => setToast(null), 4000);
      return;
    }

    setSendingRejection(true);

    const rejectedMember = memberToReject;
    let membershipUpdated = false;
    let rejectionMessageSent = false;

    try {
      const res = await fetch(`${baseUrl}/group-members/${rejectedMember.documentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: { approvalStatus: 'canceled' } }),
      });

      if (!res.ok) throw new Error('Failed to reject member');

      membershipUpdated = true;

      const emailRes = await fetch(`${baseUrl}/email/send-rejection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          groupId: group.id,
          groupMemberDocumentId: rejectedMember.documentId,
          note,
        }),
      });

      if (!emailRes.ok) throw new Error('Failed to send rejection message');

      rejectionMessageSent = true;

      setToast({
        message: t.groupTabs.rejectSuccess.replace('{name}', rejectedMember.user.displayName || rejectedMember.user.username),
        type: 'success',
      });
    } catch (error) {
      console.error('src/app/groups/[documentId]/page.tsx', error);
      setToast({
        message: membershipUpdated
          ? t.groupTabs.rejectMessageFailed.replace('{name}', rejectedMember.user.displayName || rejectedMember.user.username)
          : t.groupTabs.rejectFailed,
        type: 'error',
      });
    } finally {
      if (membershipUpdated) {
        setMembers(prev => prev.map(m => m.id === rejectedMember.id ? { ...m, approvalStatus: 'canceled' } : m));

        try {
          await fetchGroupMembers(true);
        } catch (error) {
          console.error('src/app/groups/[documentId]/page.tsx', error);
          // Best-effort refresh; local state already reflects the rejection.
        }

        if (rejectionMessageSent) {
          setShowRejectModal(false);
          setMemberToReject(null);
          setRejectionNote('');
        }
      }

      setSendingRejection(false);
      setTimeout(() => setToast(null), 4000);
    }
  };

  const handleRoleChange = async (member: GroupMemberDetail, newRole: GroupRole) => {
    if (member.role === newRole) return;

    if (member.role === 'admin' && newRole !== 'admin' && approvedAdminCount <= 1) {
      setToast({ message: 'At least one admin is required. Assign another admin first.', type: 'error' });
      setTimeout(() => setToast(null), 4000);
      return;
    }

    try {
      const res = await fetch(`${baseUrl}/group-members/${member.documentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: { role: newRole } }),
      });
      if (!res.ok) throw new Error('Failed to update role');

      setMembers(prev => prev.map(m => m.id === member.id ? { ...m, role: newRole } : m));
      await fetchGroupMembers(true);
      setToast({ message: `Role updated to ${newRole}`, type: 'success' });
    } catch (error) {
      console.error('src/app/groups/[documentId]/page.tsx', error);
      setToast({ message: 'Failed to update role', type: 'error' });
    }
    setTimeout(() => setToast(null), 4000);
  };

  const handleBulkRemove = async () => {
    const membersToRemove = approvedMembers.filter((m) => bulkEmail.selectedMemberIds.includes(m.user.id));
    if (membersToRemove.length === 0) return;

    const adminBeingRemoved = membersToRemove.filter((m) => m.role === 'admin').length;
    if (adminBeingRemoved >= approvedAdminCount) {
      setToast({ message: 'At least one admin is required. Assign another admin first.', type: 'error' });
      setTimeout(() => setToast(null), 4000);
      return;
    }

    const confirmed = window.confirm(
      t.groupTabs.bulkRemoveConfirm.replace('{count}', String(membersToRemove.length)),
    );
    if (!confirmed) return;

    try {
      await Promise.all(
        membersToRemove.map(async (member) => {
          const res = await fetch(`${baseUrl}/group-members/${member.documentId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ data: { approvalStatus: 'canceled' } }),
          });
          if (!res.ok) throw new Error(`Failed to remove member ${member.documentId}: ${res.status}`);
        }),
      );
      setMembers((prev) =>
        prev.map((m) =>
          bulkEmail.selectedMemberIds.includes(m.user.id) ? { ...m, approvalStatus: 'canceled' } : m,
        ),
      );
      await fetchGroupMembers(true);
      setToast({ message: 'Members removed from group.', type: 'success' });
    } catch (error) {
      console.error('src/app/groups/[documentId]/page.tsx', error);
      await fetchGroupMembers(true);
      setToast({ message: t.groupTabs.bulkRemoveFailed, type: 'error' });
    }
    setTimeout(() => setToast(null), 4000);
  };

  const getTimeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    if (days === 1) return '1 day ago';
    if (days < 7) return `${days} days ago`;
    if (days < 14) return '1 week ago';
    return `${Math.floor(days / 7)} weeks ago`;
  };

  const getMemberDisplayName = (member: GroupMemberDetail) => {
    return getUserData(member.user.id)?.displayName || member.user.displayName || member.user.username;
  };

  const renderMemberIdentity = (member: GroupMemberDetail) => {
    const userData = getUserData(member.user.id);
    const avatarUrl = userData?.avatarUrl || (member.user.avatarUrl ? getStrapiImageUrl(member.user.avatarUrl) : null);
    const displayName = getMemberDisplayName(member);
    const initials = userData?.initials || member.user.initials || member.user.username.substring(0, 2).toUpperCase();
    const avatarColor = userData?.avatarColor || member.user.avatarColor || '#00423E';

    return (
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 rounded-full overflow-hidden shrink-0 flex items-center justify-center text-xs font-medium text-white" style={{ backgroundColor: avatarUrl ? undefined : avatarColor }}>
          {avatarUrl ? <Image src={avatarUrl} alt={displayName} width={36} height={36} className="w-full h-full object-contain" unoptimized /> : initials}
        </div>
        <p className="font-medium text-foreground truncate">{displayName}</p>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* MWBI-890 — the in-tab visibility button was removed. Member-list
          visibility is now controlled only from the Edit Group form, so there
          is one place to change it instead of three. */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground" data-testid="group-members-tab-heading">Members ({group.memberCount ?? approvedMembers.length})</h2>
      </div>

      {/* MWBI-890 — Forum-style gate. The skeletons are empty divs, never the
          fetched members: a non-member must not receive the rows at all, since
          blur hides nothing from devtools or assistive tech. */}
      {!canViewMembers ? (
        <HiddenListGate
          title={t.groupDetail.memberListHiddenTitle}
          message={t.groupDetail.onlyMembersCanViewMemberList}
          testIdPrefix="group-members-tab"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="bg-background-secondary rounded-lg p-4 h-24" />
            ))}
          </div>
        </HiddenListGate>
      ) : (
        <>
          {/* Member Requests - only visible to admin */}
          {isAdmin && (
            <div className="space-y-3">
              <h3 className="text-lg font-medium text-foreground" data-testid="group-members-tab-pending-heading">Member requests ({pendingMembers.length})</h3>
              {pendingMembers.length === 0 ? (
                <div data-testid="group-members-tab-pending-empty-state" className="text-sm text-foreground-muted">No pending requests</div>
              ) : (
                <div className="bg-white border border-border rounded-lg overflow-x-auto">
                  <table className="w-full min-w-[720px]">
                    <thead className="bg-primary border-b border-border">
                      <tr>
                        <th className="text-left text-xs font-semibold text-white px-4 py-3">Name</th>
                        <th className="text-left text-xs font-semibold text-white px-4 py-3">Role</th>
                        <th className="text-left text-xs font-semibold text-white px-4 py-3">Request Date</th>
                        <th className="text-right text-xs font-semibold text-white px-4 py-3">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingMembers.map((member) => (
                        <tr key={member.id} data-testid={`group-members-tab-pending-row-${member.id}`} className="border-t border-border">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2 min-w-0">
                              {renderMemberIdentity(member)}
                              {member.user.ageGroup === 'age_18_plus' ? (
                                <span className="shrink-0 text-xs font-medium px-1.5 py-0.5 rounded bg-green-100 text-green-700">{t.groupTabs.ageTagAdult}</span>
                              ) : member.user.ageGroup === 'under_18' ? (
                                <span className="shrink-0 text-xs font-medium px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">{t.groupTabs.ageTagTeen}</span>
                              ) : null}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-foreground-muted capitalize">{member.role}</td>
                          <td className="px-4 py-3 text-sm text-foreground-muted">{getTimeAgo(member.createdAt)}</td>
                          <td className="px-4 py-3 text-right">
                            <select
                              value={pendingMemberActionValues[member.id] ?? ''}
                              onChange={(e) => {
                                const action = e.target.value;
                                resetPendingMemberAction(member.id);
                                if (action === 'accept') void handleAccept(member);
                                if (action === 'reject') handleReject(member);
                              }}
                              data-testid="group-members-tab-member-action-select"
                              className="text-sm border border-border rounded px-2 py-1 bg-background text-foreground"
                            >
                              <option value="" disabled>Select</option>
                              <option value="accept">Accept</option>
                              <option value="reject">Reject</option>
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Current Members */}
          <div className="space-y-3">
            <h3 className="text-lg font-medium text-foreground" data-testid="group-members-tab-current-members-heading">Current Members</h3>
            {(canSendBulkEmail || isAdmin) && bulkEmail.selectedMemberIds.length > 0 && (
              <div className="flex items-center gap-3 p-3 bg-background-secondary rounded-lg flex-wrap">
                <span className="text-sm text-foreground-muted">
                  {bulkEmail.selectedMemberIds.length} {t.groupTabs.selectedItems}
                </span>
                {canSendBulkEmail && (
                  <button
                    type="button"
                    onClick={() => bulkEmail.setShowBulkEmailModal(true)}
                    data-testid="group-members-tab-send-bulk-email-btn"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary text-background rounded-md hover:opacity-90"
                  >
                    <Mail size={14} />
                    {t.groupTabs.sendEmail}
                  </button>
                )}
                {isAdmin && (
                  <button
                    type="button"
                    onClick={() => void handleBulkRemove()}
                    data-testid="group-members-tab-bulk-remove-btn"
                    className="px-3 py-1.5 text-sm bg-primary text-background rounded-md hover:opacity-90"
                  >
                    {t.groupTabs.bulkRemove}
                  </button>
                )}
              </div>
            )}
            {approvedMembers.length === 0 ? (
              <div data-testid="group-members-tab-no-members-state" className="text-center py-12 text-foreground-muted">No members yet</div>
            ) : (
              <div className="bg-white border border-border rounded-lg overflow-x-auto">
                <table className="w-full min-w-0">
                  <thead className="bg-primary border-b border-border">
                    <tr>
                      {(canSendBulkEmail || isAdmin) && (
                        <th className="w-10 px-3 py-3 sm:px-4">
                          <input
                            type="checkbox"
                            checked={approvedMembers.length > 0 && bulkEmail.selectedMemberIds.length === approvedMembers.length}
                            onChange={bulkEmail.toggleSelectAllMembers}
                            aria-label={t.groupTabs.selectAllMembers}
                            data-testid="group-members-tab-select-all-checkbox"
                            className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                          />
                        </th>
                      )}
                      <th className="text-left text-xs font-semibold text-white px-3 py-3 sm:px-4">Name</th>
                      <th className="text-left text-xs font-semibold text-white px-3 py-3 sm:px-4">Role</th>
                      <th className="hidden sm:table-cell text-left text-xs font-semibold text-white px-4 py-3">Member Since</th>
                    </tr>
                  </thead>
                  <tbody>
                    {approvedMembers.map((member) => {
                      const memberSince = new Date(member.createdAt).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', { month: 'short', year: 'numeric' });
                      return (
                        <tr key={member.id} data-testid={`group-members-tab-member-row-${member.id}`} className="border-t border-border">
                          {(canSendBulkEmail || isAdmin) && (
                            <td className="px-3 py-3 sm:px-4 align-middle">
                              <input
                                type="checkbox"
                                checked={bulkEmail.selectedMemberIds.includes(member.user.id)}
                                onChange={() => bulkEmail.toggleMemberSelection(member.user.id)}
                                aria-label={t.groupTabs.selectMember.replace('{name}', getMemberDisplayName(member))}
                                data-testid="group-members-tab-member-bulk-checkbox"
                                className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                              />
                            </td>
                          )}
                          <td className="px-3 py-3 sm:px-4">
                            <div className="min-w-0">
                              {renderMemberIdentity(member)}
                              <p className="sm:hidden text-xs text-foreground-muted mt-1 pl-12">{memberSince}</p>
                            </div>
                          </td>
                          <td className="px-3 py-3 sm:px-4">
                            {isAdmin ? (
                              <select
                                value={member.role}
                                onChange={(e) => handleRoleChange(member, e.target.value as GroupRole)}
                                data-testid="group-members-tab-member-role-select"
                                className="text-sm border border-border rounded px-2 py-1 bg-background text-foreground"
                              >
                                <option value="admin">Admin</option>
                                <option value="editor">Editor</option>
                                <option value="member">Member</option>
                              </select>
                            ) : (
                              <span className="text-sm text-foreground-muted capitalize">{member.role}</span>
                            )}
                          </td>
                          <td className="hidden sm:table-cell px-4 py-3 text-sm text-foreground-muted">
                            {memberSince}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      <div data-testid="group-members-tab-reject-modal-root">
        <Modal
          isOpen={showRejectModal}
          onClose={closeRejectModal}
          title={t.groupTabs.rejectModalTitle}
          subtitle={t.groupTabs.rejectModalSubtitle.replace('{name}', memberToReject?.user.displayName || memberToReject?.user.username || '')}
          footer={
            <>
              <button
                type="button"
                onClick={closeRejectModal}
                disabled={sendingRejection}
                data-testid="group-members-tab-reject-modal-cancel-btn"
                className="px-4 py-2 text-sm border border-border rounded-md text-foreground hover:bg-background-secondary disabled:cursor-not-allowed disabled:opacity-60"
              >
                {t.groupTabs.cancel}
              </button>
              <button
                type="button"
                onClick={handleConfirmReject}
                disabled={sendingRejection}
                data-testid="group-members-tab-reject-modal-submit-btn"
                className="px-4 py-2 text-sm bg-primary text-background rounded-md hover:opacity-90 disabled:cursor-not-allowed"
              >
                {sendingRejection ? t.groupTabs.sendingRejection : t.groupTabs.rejectAndSend}
              </button>
            </>
          }
        >
          <div>
            <label htmlFor="group-rejection-note" className="block text-sm font-medium text-foreground mb-1.5">
              {t.groupTabs.rejectionReasonLabel} *
            </label>
            <textarea
              id="group-rejection-note"
              value={rejectionNote}
              onChange={(event) => setRejectionNote(event.target.value)}
              placeholder={t.groupTabs.rejectionReasonPlaceholder}
              rows={5}
              data-testid="group-members-tab-rejection-note-textarea"
              className="w-full px-3 py-2 border border-border rounded-md text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            />
          </div>
        </Modal>
      </div>

      <BulkEmailModal
        isOpen={bulkEmail.showBulkEmailModal}
        onClose={bulkEmail.closeBulkEmailModal}
        selectedCount={bulkEmail.selectedMemberIds.length}
        subject={bulkEmail.bulkEmailSubject}
        onSubjectChange={bulkEmail.setBulkEmailSubject}
        message={bulkEmail.bulkEmailMessage}
        onMessageChange={bulkEmail.setBulkEmailMessage}
        sending={bulkEmail.sendingBulkEmail}
        onSend={bulkEmail.handleSendBulkEmail}
      />
    </div>
  );
}

